import { DrawScopedSectionFieldCollection, SectionFieldCollection } from '../magic-report.type';

/**
 * Draw-scoped sections
 * =====================
 * A Magic Report can cover several project draws. Sections listed in
 * {@link DRAW_SCOPED_SECTION_KEYS} hold one independent copy of their content per draw.
 *
 * Rather than teaching the shared section machinery about draws, each per-draw copy simply lives
 * under its own key in `reportState.sections` — `application-comments__<drawId>` — so
 * `MagicReportSection` and the action-items utilities keep treating it as an ordinary section.
 * The array shape the backend stores is translated to and from these keys in exactly two places
 * (see {@link fromApiDrawScopedSections} / {@link toApiDrawScopedSections}).
 *
 * To make another section draw-scoped later: add its key here and pass a `drawId` to the
 * component so it can build its config id with {@link drawScopedKey}.
 */
export const DRAW_SCOPED_SECTION_KEYS = ['application-comments', 'reduction-comments'] as const;

export type DrawScopedSectionKey = (typeof DRAW_SCOPED_SECTION_KEYS)[number];

/**
 * Separator between a section key and its draw id. Deliberately `__` and not `::` — these strings
 * are also used as DOM ids that get matched with `querySelectorAll`, where a colon reads as a
 * CSS pseudo-element and would need escaping.
 */
const DRAW_KEY_SEPARATOR = '__';

export const drawScopedKey = (baseKey: string, drawId: string): string => `${baseKey}${DRAW_KEY_SEPARATOR}${drawId}`;

/** Splits `application-comments__abc` into its base key and draw id; null if not draw-scoped. */
export const parseDrawScopedKey = (key: string): { baseKey: string; drawId: string } | null => {
  const separatorIndex = key.indexOf(DRAW_KEY_SEPARATOR);
  if (separatorIndex <= 0) return null;
  const baseKey = key.slice(0, separatorIndex);
  const drawId = key.slice(separatorIndex + DRAW_KEY_SEPARATOR.length);
  if (!drawId) return null;
  return { baseKey, drawId };
};

export const isDrawScopedSectionKey = (baseKey: string): baseKey is DrawScopedSectionKey =>
  (DRAW_SCOPED_SECTION_KEYS as readonly string[]).includes(baseKey);

/** Strips the draw suffix from a key, leaving keys that carry no suffix untouched. */
export const stripDrawScope = (key: string): string => parseDrawScopedKey(key)?.baseKey ?? key;

export const getDrawScopedSection = (sections: any, baseKey: string, drawId: string): SectionFieldCollection | undefined =>
  sections?.[drawScopedKey(baseKey, drawId)];

/**
 * Every stored copy of a draw-scoped section, ordered by `drawOrder` when supplied so callers
 * render draws in the report's own order rather than object-key order.
 */
export const collectDrawScopedSections = (
  sections: any,
  baseKey: string,
  drawOrder?: string[],
): Array<{ drawId: string; collection: SectionFieldCollection }> => {
  if (!sections) return [];

  const found = new Map<string, SectionFieldCollection>();
  Object.entries(sections).forEach(([key, value]) => {
    if (!value) return;
    const parsed = parseDrawScopedKey(key);
    if (parsed?.baseKey === baseKey) {
      found.set(parsed.drawId, value as SectionFieldCollection);
    }
  });

  if (!drawOrder?.length) {
    return Array.from(found, ([drawId, collection]) => ({ drawId, collection }));
  }

  return drawOrder.filter(drawId => found.has(drawId)).map(drawId => ({ drawId, collection: found.get(drawId)! }));
};

/**
 * Backend array -> flat `sections` keys.
 *
 * Legacy single-draw reports stored a bare object rather than an array, and the backend upgrades
 * those to a single element with no `drawId`. Such an element is bound to `fallbackDrawId` (the
 * report's first draw) so pre-existing comments keep showing up.
 */
export const fromApiDrawScopedSections = (
  value: DrawScopedSectionFieldCollection[] | SectionFieldCollection | undefined | null,
  baseKey: string,
  fallbackDrawId?: string,
): Record<string, SectionFieldCollection> => {
  if (!value) return {};

  const elements: DrawScopedSectionFieldCollection[] = Array.isArray(value) ? value : [value as DrawScopedSectionFieldCollection];

  const mapped: Record<string, SectionFieldCollection> = {};
  elements.forEach(element => {
    if (!element) return;
    const drawId = element.drawId || fallbackDrawId;
    if (!drawId) return;
    const { drawId: _ignored, ...collection } = element as DrawScopedSectionFieldCollection;
    mapped[drawScopedKey(baseKey, drawId)] = collection as SectionFieldCollection;
  });
  return mapped;
};

/**
 * Flat `sections` keys -> backend array.
 *
 * Always emits EVERY draw's copy: the sections endpoint replaces these fields wholesale, so a
 * partial array would silently delete the draws left out of it.
 */
export const toApiDrawScopedSections = (
  sections: any,
  baseKey: string,
  drawOrder?: string[],
): DrawScopedSectionFieldCollection[] | undefined => {
  const collected = collectDrawScopedSections(sections, baseKey, drawOrder);
  if (collected.length === 0) return undefined;
  return collected.map(({ drawId, collection }) => ({ ...collection, drawId }));
};
